/**
 * Typed error classes for the PDPO Adviser application.
 *
 * These provide user-friendly messages while preserving the original error
 * for logging. Use `toUserMessage()` to get a safe string for UI display.
 */

// ---------------------------------------------------------------------------
// Base
// ---------------------------------------------------------------------------

export class AppError extends Error {
  /** Safe message that can be shown to end-users */
  readonly userMessage: string;
  /** Original upstream error (Supabase, fetch, etc.) */
  readonly cause?: unknown;

  constructor(userMessage: string, cause?: unknown) {
    super(userMessage);
    this.name = 'AppError';
    this.userMessage = userMessage;
    this.cause = cause;
  }
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export class AuthError extends AppError {
  constructor(message = 'Please sign in to continue.', cause?: unknown) {
    super(message, cause);
    this.name = 'AuthError';
  }
}

// ---------------------------------------------------------------------------
// Not Found
// ---------------------------------------------------------------------------

export class NotFoundError extends AppError {
  constructor(resource: string, cause?: unknown) {
    super(`${resource} not found.`, cause);
    this.name = 'NotFoundError';
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export class ValidationError extends AppError {
  readonly field?: string;

  constructor(message: string, field?: string, cause?: unknown) {
    super(message, cause);
    this.name = 'ValidationError';
    this.field = field;
  }
}

// ---------------------------------------------------------------------------
// Insufficient Credits
// ---------------------------------------------------------------------------

export class InsufficientCreditsError extends AppError {
  readonly required: number;
  readonly available: number;

  constructor(required: number, available: number, cause?: unknown) {
    super(
      `Not enough credits. You need ${required} but have ${available}.`,
      cause,
    );
    this.name = 'InsufficientCreditsError';
    this.required = required;
    this.available = available;
  }
}

// ---------------------------------------------------------------------------
// Database / Supabase
// ---------------------------------------------------------------------------

export class DatabaseError extends AppError {
  constructor(operation: string, cause?: unknown) {
    super(`Something went wrong while ${operation}. Please try again.`, cause);
    this.name = 'DatabaseError';
  }
}

// ---------------------------------------------------------------------------
// Network / Fetch
// ---------------------------------------------------------------------------

export class NetworkError extends AppError {
  readonly status?: number;

  constructor(operation: string, status?: number, cause?: unknown) {
    const msg =
      status === 429
        ? 'Too many requests. Please wait a moment and try again.'
        : `Unable to complete ${operation}. Please check your connection and try again.`;
    super(msg, cause);
    this.name = 'NetworkError';
    this.status = status;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract a user-safe message from any thrown value.
 *
 * - If it's an `AppError`, returns `userMessage`.
 * - If it's a generic `Error`, returns a generic fallback.
 * - Otherwise returns a default string.
 */
export function toUserMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err instanceof AppError) return err.userMessage;
  return fallback;
}

/**
 * Wrap a raw Supabase `{ data, error }` result. If `error` is truthy,
 * throws a `DatabaseError` with the given operation context.
 */
export function throwIfSupabaseError<T>(
  result: { data: T; error: unknown },
  operation: string,
): T {
  if (result.error) {
    throw new DatabaseError(operation, result.error);
  }
  return result.data;
}
